import React, { useState } from 'react';
import { Download, Shield, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/httpErrors';

interface SecurityFeature {
  feature: string;
  status: string;
}

interface ComplianceGate {
  name: string;
  status: 'active' | 'completed' | 'pending';
}

const CompliancePage: React.FC = () => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [downloadingWISP, setDownloadingWISP] = useState(false);

  const firmId = user?.firm || '';

  if (!user || (user.role !== 'cpa' && user.role !== 'super_admin')) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">Access denied. Compliance pages require CPA or admin role.</p>
      </div>
    );
  }

  const securityFeatures: SecurityFeature[] = [
    { feature: 'Data Encryption (At Rest)', status: 'AES-256 / Azure SQL' },
    { feature: 'Transport Encryption (In Transit)', status: 'TLS 1.2+' },
    { feature: 'AI Provider', status: 'Anthropic PBC (no model training)' },
    { feature: 'Sub-processors', status: 'Azure + Render + Anthropic' },
    { feature: 'Data Retention', status: '90 days post-engagement close' },
    { feature: 'Breach Notification SLA', status: '<48 hours to firm admin' },
  ];

  const complianceGates: ComplianceGate[] = [
    { name: 'ToS Clickwrap (COMPLY-1)', status: 'active' },
    { name: '§7216 Tax Consent (COMPLY-2)', status: 'active' },
    { name: 'Report Acknowledgment (COMPLY-3)', status: 'active' },
    { name: 'Compliance Intake (COMPLY-5)', status: 'active' },
    { name: 'GDPR Hold Flag', status: 'active' },
    { name: 'Data Deletion Requests (COMPLY-7)', status: 'active' },
  ];

  const handleDownloadWISP = async () => {
    setDownloadingWISP(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `/api/v1/admin/wisp/${firmId}?format=pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to download WISP: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WISP-${firmId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDownloadingWISP(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-8 h-8 text-blue-600" />
        <h1 className="text-3xl font-bold">Compliance & Security</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Section A: Platform Security Summary */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Platform Security Summary</h2>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 font-semibold text-gray-700">Feature</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {securityFeatures.map((feature, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-3 text-gray-900">{feature.feature}</td>
                  <td className="px-6 py-3 text-gray-700 text-sm">{feature.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section B: Written Information Security Plan */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Written Information Security Plan (WISP)</h2>
        <p className="text-gray-600 mb-4">
          This WISP documents Yansar's security measures and compliance with the FTC Safeguards Rule
          (16 C.F.R. Part 314) and GLBA Privacy Rule. Use this document to satisfy audit and
          regulatory inquiries.
        </p>
        <button
          onClick={handleDownloadWISP}
          disabled={downloadingWISP}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          <Download className="w-4 h-4" />
          {downloadingWISP ? 'Downloading...' : `Download WISP for ${user?.firm || 'Your Firm'}`}
        </button>
      </section>

      {/* Section C: Active Compliance Gates */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Active Compliance Gates</h2>
        <p className="text-gray-600 mb-4">
          The following compliance controls are active across all engagements on this platform.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {complianceGates.map((gate, idx) => (
            <div
              key={idx}
              className="p-4 bg-white border border-green-200 rounded-lg bg-green-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">{gate.name}</p>
                  <p className="text-sm text-green-700">Active</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section D: Regulatory References */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Regulatory References</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
          <p>
            <strong>Gramm-Leach-Bliley Act (GLBA)</strong>
            {' '}
            — 15 U.S.C. § 6801 et seq.
          </p>
          <p>
            <strong>FTC Safeguards Rule</strong>
            {' '}
            — 16 C.F.R. Part 314 (information security standards for financial institutions)
          </p>
          <p>
            <strong>FTC Privacy Rule</strong>
            {' '}
            — 16 C.F.R. Part 318 (customer privacy protection)
          </p>
          <p>
            <strong>AICPA SSCS No. 1</strong>
            {' '}
            — Statement on Standards for Consulting Services (CPA professional standards)
          </p>
          <p>
            <strong>IRS Section 7216</strong>
            {' '}
            — Confidentiality requirements for tax consultants and return preparers
          </p>
        </div>
      </section>
    </div>
  );
};

export default CompliancePage;
